import { STAFF_PATHS } from '../utils/staffPaths';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { TbLogout, TbMenu2, TbSettings, TbLayoutSidebarLeftCollapse, TbLayoutSidebarLeftExpand } from 'react-icons/tb';
import { removeAdminToken } from '../services/authService';
import { useInternalStaff } from './InternalStaffRoute';
import ThemeToggle from '../v2/shell/ThemeToggle';
import StaffDesign from './StaffDesign';
import StaffSetupBanner from './StaffSetupBanner';
import { staffGroupsFor, staffPath, findStaffItem, STAFF_SETTINGS } from './staffNav';
import '../v2/shell/shell.css';
import '../v2/components/primitives.css';
import './MarketingDashboard.css';

// The staff portal's shell. It is built from the customer shell's own pieces — the ink
// rail, the 44px top bar with its breadcrumb, the appearance switch, the phone drawer and
// the page heading — rather than a look-alike, so the two interfaces cannot drift apart.
//
// Unlike the customer shell the rail lists every page under a group heading instead of
// opening hover flyouts: staff have more destinations and use them less often, and a
// list that can be scanned beats four icons that have to be explored.
//
// `section` is a key from staffNav.js; the title, description and breadcrumb come from
// there. `native` wraps the page in .staff-workspace, which styles bare buttons and
// inputs — right for the pages written for this portal, wrong for the administration
// screens, which bring their own Bootstrap and MUI controls.
export default function StaffLayout({ section, title, description, children, showSetupBanner = true, native = true }) {
  const staff = useInternalStaff();
  const navigate = useNavigate();
  const location = useLocation();
  const role = staff?.role === 'admin' ? 'Administrator' : 'Marketing · Read only';
  const groups = staffGroupsFor(staff?.role);
  const found = findStaffItem(section);

  const [railOpen, setRailOpen] = useState(() => {
    try { return localStorage.getItem('staffRailOpen') !== '0'; } catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem('staffRailOpen', railOpen ? '1' : '0'); } catch { /* private mode */ }
  }, [railOpen]);

  // Any navigation closes the phone drawer and the account panel, and starts the new page
  // at the top: .v2-main is the scroller, not the window.
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const mainRef = useRef(null);
  useEffect(() => {
    setDrawerOpen(false);
    setAccountOpen(false);
    if (mainRef.current) mainRef.current.scrollTop = 0;
  }, [location.pathname]);
  useEffect(() => {
    if (!drawerOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setDrawerOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  const signOut = () => { removeAdminToken(); navigate(STAFF_PATHS.login); };
  const accountRef = useRef(null);
  const home = staff?.role === 'admin' ? STAFF_PATHS.dashboard : STAFF_PATHS.marketing;
  const pageTitle = title || found?.item.label || 'Staff portal';
  const pageDescription = description ?? found?.item.desc;

  return <StaffDesign className="staff-shell-root">
    {showSetupBanner && <StaffSetupBanner />}
    <div className={`v2-shell staff-shell${railOpen ? ' rail-open' : ''}${drawerOpen ? ' drawer-open' : ''}`}>
      <button type="button" className="v2-nav-scrim" aria-label="Close menu"
              tabIndex={drawerOpen ? 0 : -1} onClick={() => setDrawerOpen(false)} />
      <nav className="v2-rail" aria-label="Staff navigation">
        <Link className="v2-rail-brand staff-rail-brand" to={home}>
          <img className="v2-mark" src="/images/pipsqueak-mark.png" alt="" />
          <span className="v2-rail-wordmark">Pipsqueak</span>
          <span className="v2-rail-wordmark staff-internal-tag">Internal</span>
        </Link>
        <button type="button" className="v2-rail-item v2-rail-toggle" aria-expanded={railOpen}
                aria-label={railOpen ? 'Collapse menu labels' : 'Expand menu labels'}
                title={railOpen ? 'Collapse menu' : 'Expand menu'} onClick={() => setRailOpen((v) => !v)}>
          {railOpen ? <TbLayoutSidebarLeftCollapse size={17} /> : <TbLayoutSidebarLeftExpand size={17} />}
          <span className="v2-rail-label">Collapse</span>
        </button>
        <div className="staff-rail-groups">
          {groups.map((group) => <div key={group.key} className="staff-rail-group" role="group" aria-label={group.label}>
            <div className="staff-rail-label" aria-hidden="true">{group.label}</div>
            {group.items.map(({ key, label, icon: Icon, ...item }) =>
              <NavLink key={key} to={staffPath({ key, ...item })} title={railOpen ? undefined : label}
                       className={({ isActive }) => `v2-rail-item${isActive ? ' on' : ''}`}>
                <Icon size={17} aria-hidden="true" />
                <span className="v2-rail-label">{label}</span>
              </NavLink>)}
          </div>)}
        </div>
        <button type="button" title="Sign out" aria-label="Sign out" className="v2-rail-item" onClick={signOut}>
          <TbLogout size={17} />
          <span className="v2-rail-label">Sign out</span>
        </button>
        <button type="button" ref={accountRef}
                className={`v2-rail-user${accountOpen || section === STAFF_SETTINGS.key ? ' on' : ''}`}
                aria-haspopup="true" aria-expanded={accountOpen} aria-label="Staff account"
                title={railOpen ? undefined : 'Staff account'} onClick={() => setAccountOpen((v) => !v)}>
          <span className="v2-avatar">{initials(staff?.name)}</span>
          <span className="v2-rail-label v2-rail-username">{staff?.name || 'Signed in'}</span>
        </button>
      </nav>

      <div className="v2-col">
        <header className="v2-topbar">
          <button type="button" className="v2-burger" aria-label="Toggle staff navigation" aria-expanded={drawerOpen}
                  onClick={() => setDrawerOpen((v) => !v)}>
            <TbMenu2 size={18} />
          </button>
          <nav className="v2-crumbs" aria-label="Breadcrumb">
            <Link className="v2-crumb" to={home}>Staff portal</Link>
            {found?.group && <><span className="v2-crumb-sep" aria-hidden="true">/</span><span>{found.group.label}</span></>}
            <span className="v2-crumb-sep" aria-hidden="true">/</span>
            <b aria-current="page">{pageTitle}</b>
          </nav>
          {/* Screens put their status or scope controls here (StaffStatus.jsx), as customer
              screens do through TopbarSlot. */}
          <div className="v2-topbar-slot" id="v2-topbar-slot" />
          <div className="v2-topbar-actions">
            <span className="staff-role-chip">{role}</span>
            <ThemeToggle />
          </div>
        </header>
        <main className="v2-main" ref={mainRef}>
          <div className="v2-page staff-page">
            <div className="v2-pagehead">
              <div>
                <div className="v2-eyebrow">{found?.group?.label || 'Staff portal'}</div>
                <h2>{pageTitle}</h2>
                {pageDescription && <p>{pageDescription}</p>}
              </div>
            </div>
            {native ? <div className="staff-workspace">{children}</div> : children}
          </div>
        </main>
      </div>
    </div>
    {accountOpen && <StaffAccountPanel staff={staff} role={role} anchorRef={accountRef}
                                       onClose={() => setAccountOpen(false)} onSignOut={signOut} />}
  </StaffDesign>;
}

const initials = (name) => (name || '?').trim().split(/\s+/).slice(0, 2).map((p) => p[0]).join('').toUpperCase();

// The account panel, outside the rail: the rail clips its overflow, and on a phone it is a
// transformed drawer that would carry a fixed panel off-screen with it. Beside the avatar
// on a desktop, a sheet across the foot of the screen on a phone — as in the customer shell.
function StaffAccountPanel({ staff, role, anchorRef, onClose, onSignOut }) {
  const panelRef = useRef(null);
  const [position, setPosition] = useState(null);
  useLayoutEffect(() => {
    const place = () => {
      const rect = anchorRef.current?.getBoundingClientRect();
      if (!rect || window.innerWidth <= 760) { setPosition(null); return; }
      setPosition({ left: rect.right + 8, bottom: Math.max(8, window.innerHeight - rect.bottom) });
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [anchorRef]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') { onClose(); anchorRef.current?.focus(); } };
    const onDown = (e) => {
      if (panelRef.current?.contains(e.target) || anchorRef.current?.contains(e.target)) return;
      onClose();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onDown); };
  }, [onClose, anchorRef]);
  return <div ref={panelRef} role="dialog" aria-label="Staff account"
              className={`v2-account staff-account${position ? '' : ' v2-account--sheet'}`} style={position || undefined}>
    <div className="v2-account-col">
      <div className="v2-account-identity">
        <span className="v2-avatar">{initials(staff?.name)}</span>
        <div style={{ minWidth: 0 }}>
          <div className="v2-account-name">{staff?.name}</div>
          <div className="v2-account-sub">{staff?.email}</div>
          <div className="v2-account-role">{role}</div>
        </div>
      </div>
      <div className="v2-account-links">
        <Link className="v2-account-link" to={STAFF_SETTINGS.path}><TbSettings size={15} />{STAFF_SETTINGS.label}</Link>
        <button type="button" className="v2-account-link" onClick={onSignOut} aria-label="Sign out of staff portal">
          <TbLogout size={15} />Sign out
        </button>
      </div>
    </div>
  </div>;
}
